import type { ReactNode } from 'react'
import { ToggleGroup, ToggleItem } from '@/components/ToggleGroup'
import { TypeBadge } from '@/features/legends/components/TypeBadge'
import type { AnswerOption } from '@/features/legends/editor/entities'
import type { TemplateDataSource } from '@/features/legends/templates/templates'
import { type RynoType, Types } from '@/features/legends/editor/ryno-types'
import type { MappingType } from '@/features/legends/templates/MappingEditorViewModel'
import styles from './MappingInspector.module.css'

const mappingTypes: { type: MappingType; label: string }[] = [
  { type: 'Manual', label: 'Manual' },
  { type: 'Expr', label: 'Expression' },
]

export interface MappingInspectorTitleProps {
  questionId: string
  valueType: RynoType | null | undefined
}

/**
 * The mapped question's ID and type, as the inspector's title.
 */
export function MappingInspectorTitle({
  questionId,
  valueType,
}: MappingInspectorTitleProps) {
  return (
    <>
      <span className={styles.questionId}>{questionId}</span>
      <TypeBadge type={valueType} />
    </>
  )
}

export interface MappingTypeToggleProps {
  value: MappingType
  onChange: (type: MappingType) => unknown
  isDisabled?: boolean
  testId?: string
}

/**
 * Picks how the template answers the question.
 */
export function MappingTypeToggle({
  value,
  onChange,
  isDisabled,
  testId,
}: MappingTypeToggleProps) {
  return (
    <ToggleGroup stretch>
      {mappingTypes.map(({ type, label }) => (
        <ToggleItem
          key={type}
          toggled={value === type}
          isDisabled={isDisabled}
          onChange={() => onChange(type)}
          testId={testId && `${testId}-${type.toLowerCase()}`}
        >
          {label}
        </ToggleItem>
      ))}
    </ToggleGroup>
  )
}

export interface MappingNoteProps {
  children?: ReactNode
}

/**
 * An explanation in the mapping inspector.
 */
export function MappingNote({ children }: MappingNoteProps) {
  return <p className={styles.note}>{children}</p>
}

export interface MappingReturnHintProps {
  valueType: RynoType | null | undefined
  possibleValues: readonly AnswerOption[]
}

/**
 * What the formula must return: the question's type, and its answers when it
 * has a fixed set.
 */
export function MappingReturnHint({
  valueType,
  possibleValues,
}: MappingReturnHintProps) {
  return (
    <>
      <div className={styles.returnHint}>
        This formula needs to return a <TypeBadge type={valueType} />
      </div>
      {possibleValues.length > 0 && (
        <div className={styles.returnHint}>
          <span>Possible values are:</span>
          <ul className={styles.values}>
            {possibleValues.map((option) => (
              <li key={option.id} className={styles.value}>
                <code className={styles.code}>{String(option.value)}</code>
                {option.text && option.text !== String(option.value) && (
                  <span className={styles.valueText}>{option.text}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}

export interface TemplateDataSourceListProps {
  sources: readonly TemplateDataSource[]
}

/**
 * The values a template's formulas can read from the job being scoped.
 */
export function TemplateDataSourceList({
  sources,
}: TemplateDataSourceListProps) {
  return (
    <div className={styles.sources}>
      {sources.map((source) => (
        <span key={source.id} className={styles.source}>
          <code className={styles.sourceId}>{source.id}</code>
          <TypeBadge type={Types.fromJS(source.valueType)} />
        </span>
      ))}
    </div>
  )
}
